import fp from 'lodash/fp';
import React, { useMemo, useRef, useCallback, useEffect, useImperativeHandle, FC, Ref } from 'react';
import { VariableSizeGrid } from 'react-window';

import { cn, useSizeContext, useMemoDeps } from 'Modules/ui';

import { calculateColumnWidths } from './calculations';
import { withSortColumnsSettings } from './SortColumnsSettings';
import { useRatios } from './RatiosColumnsSettings';
import { Header } from './Header';
import { Filters } from './Filters';
import { Body } from './Body';
import { CheckboxHeader } from './CheckboxHeader';
import { FactorinGridProps, FactorinGridRef } from './types';
import styles from './index.module.scss';


export * from './common';
export * from './types';

const ROW_HEIGHT_REM = parseFloat(styles.rowHeight);
const CHECKBOX_COLUMN_WIDTH_REM = parseFloat(styles.checkBoxColumnWidth);

const FactorinGridComponent: FC<
FactorinGridProps & { componentRef: Ref<FactorinGridRef>, userSetVScrollBarVisible?: boolean }
> = ({
    componentRef,
    disabled,
    width, height,
    columns,
    ratioSaveKey,
    itemCount,
    getData,
    headerHeightRem = ROW_HEIGHT_REM,
    rowHeightRem = ROW_HEIGHT_REM,
    onItemsRendered,
    selectedRow,
    onRowClick,
    checkboxWidthRem = CHECKBOX_COLUMN_WIDTH_REM,
    checkable,
    disableCheckAll,
    checkedRows,
    onCheckedChanged,
    checkedKeySelector,
    customCheckboxCell,
    customCheckboxHeader,
    customCheckbox,
    isHovering,
    hideVScroll,
    disableResizeColumns,
    classNames = { cell: {} },
    path,
}) => {
    const { rem } = useSizeContext();
    const rowHeight = useMemo(() => fp.constant(rowHeightRem * rem), [rem, rowHeightRem]);

    const withFilters = useMemoDeps(
        fp.some(fp.get('filterField')),
        [columns],
    );
    const headerHeight = headerHeightRem * rem;

    const checkboxWidth = checkboxWidthRem * rem;

    const bodyHeight = Math.max(0, height - headerHeight * (withFilters ? 2 : 1));
    const bodyWidth = width - (checkable ? checkboxWidth : 0);

    const vScrollBarVisible = itemCount * rowHeightRem * rem > bodyHeight;
    const headerWidth = bodyWidth;

    const bodyRef = useRef<VariableSizeGrid>();
    const onChangeRatios = useCallback(
        scrollLeft => bodyRef.current.scrollTo({ scrollLeft }),
        [],
    );

    const { ratios, updateRatios, resetRatios } = useRatios(
        columns,
        `${ratioSaveKey ?? path}.ratios`,
        onChangeRatios,
        bodyWidth,
    );

    const columnWidths = useMemoDeps(
        calculateColumnWidths,
        [headerWidth, columns, ratios, rem],
    );

    const hScrollBarVisible = useMemo(
        () => fp.sum(columnWidths) > headerWidth,
        [columnWidths, headerWidth],
    );

    const getColumnWidth = useCallback(index => columnWidths[index] || 0, [columnWidths]);

    const headerRef = useRef<VariableSizeGrid>();
    const checkboxColumnRef = useRef<VariableSizeGrid>();
    const filtersRef = useRef<VariableSizeGrid>();

    useImperativeHandle(componentRef, () => ({
        resetAfterIndex: (index, shouldForceUpdate) =>
            bodyRef.current.resetAfterIndices({
                rowIndex: index,
                columnIndex: 0,
                shouldForceUpdate,
            }),
    }));

    useEffect(() => {
        bodyRef.current.resetAfterIndices({ rowIndex: 0, columnIndex: 0 });
        if (filtersRef.current)
            filtersRef.current.resetAfterIndices({ rowIndex: 0, columnIndex: 0 });
        if (checkboxColumnRef.current)
            checkboxColumnRef.current.resetAfterIndices({ rowIndex: 0, columnIndex: 0 });
    }, [columnWidths]);

    return (
        <div className={cn(styles.container, classNames.container)}>
            {checkable && (
                <CheckboxHeader
                    width={checkboxWidth}
                    height={headerHeight}
                    disabled={disabled}
                    disableCheckAll={disableCheckAll}
                    checkedRows={checkedRows}
                    onCheckedChanged={onCheckedChanged}
                    checkbox={customCheckbox}
                    itemCount={itemCount}
                    className={classNames.checkboxHeader}
                    withFilters={withFilters}
                    filterHeader={customCheckboxHeader}
                />
            )}
            <Header
                ref={headerRef}
                width={headerWidth}
                height={headerHeight}
                style={{ left: checkable ? checkboxWidth : 0 }}
                checkable={!!checkable}
                columns={columns}
                columnWidths={columnWidths}
                onResizeFinished={updateRatios}
                onResizeReset={resetRatios}
                disableResizeColumns={disableResizeColumns}
                classNames={{
                    container: classNames.header,
                    cell: fp.get('cell.header', classNames),
                }}
            />
            {withFilters && (
                <Filters
                    ref={filtersRef}
                    width={headerWidth}
                    height={headerHeight}
                    style={{
                        left: checkable ? checkboxWidth : 0,
                        top: headerHeight,
                    }}
                    classNames={{
                        container: classNames.filters,
                        cell: fp.get('cell.filterField', classNames),
                    }}
                    columns={columns}
                    columnWidth={getColumnWidth}
                />
            )}
            <Body
                ref={bodyRef}
                checkboxRef={checkboxColumnRef}
                top={withFilters ? headerHeight * 2 : headerHeight}
                width={bodyWidth}
                height={bodyHeight}
                columns={columns}
                columnWidth={getColumnWidth}
                getData={getData}
                hScrollBarVisible={hScrollBarVisible}
                vScrollBarVisible={vScrollBarVisible}
                hideVScroll={hideVScroll}
                rowCount={itemCount}
                rowHeight={rowHeight}
                checkable={checkable}
                checkboxWidth={checkboxWidth}
                checkboxCell={customCheckboxCell}
                checkboxComponent={customCheckbox}
                checkedKeySelector={checkedKeySelector}
                checkedRows={checkedRows}
                onCheckedChanged={onCheckedChanged}
                disabled={disabled}
                onRowClick={onRowClick}
                onItemsRendered={onItemsRendered}
                selectedRow={selectedRow}
                onScroll={useCallback(
                    ({ scrollLeft, scrollTop }) => {
                        if (checkboxColumnRef.current)
                            checkboxColumnRef.current.scrollTo({ scrollTop });
                        if (filtersRef.current)
                            filtersRef.current.scrollTo({ scrollLeft });
                        headerRef.current.scrollTo({ scrollLeft });
                    },
                    [],
                )}
                isHovering={isHovering}
                classNames={{
                    body: {
                        container: classNames.body,
                        cell: fp.get('cell.body', classNames),
                    },
                    checkbox: fp.get('cell.checkbox', classNames),
                }}
            />
        </div>
    );
};

const withForwardRef = Component => React.forwardRef((props, ref) => (
    <Component componentRef={ref} {...props} />
));

export const FactorinGrid: FC<FactorinGridProps> = fp.compose(
    withForwardRef,
    withSortColumnsSettings,
    React.memo,
)(FactorinGridComponent);
